import React, { useEffect, useState } from 'react';
import BottomSheet from '../components/BottomSheet';
import Avatar from '../components/Avatar';
import StarRating from '../components/StarRating';
import PillBadge from '../components/PillBadge';
import { formatAbsoluteDate } from '../utils/date';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import { canShowPurchaseCta } from '../utils/storeCompliance';

// Droits du plan : la génération de réponse IA n'est pas incluse partout.
export default function ReviewDetailModal({
  review,
  open,
  onClose,
  gmbConnected = false,
  onGenerateAi,
  onPublish,
  onIgnore,
  fallbackMode = false,
}) {
  // Fallback permissif tant que l'API n'a pas répondu : ne pas afficher un
  // cadenas à un abonné qui y a droit. Le serveur reste la barrière réelle.
  const { capabilities } = useCurrentRestaurant();
  const peutGenererIa = capabilities ? capabilities.ai_reply === true : true;

  const [draft, setDraft] = useState('');
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [ignoring, setIgnoring] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (review) {
      setDraft(review.reply_text || '');
      setError(null);
    }
  }, [review]);

  if (!review) {
    return <BottomSheet open={open} onClose={onClose} title="" height="auto" />;
  }

  const isPublished = review.reply_status === 'published';
  const hasReplyId = !!review.reply_id;

  const handleGenerate = async () => {
    setError(null);
    setGenerating(true);
    try {
      const text = await onGenerateAi(review);
      setDraft(text || '');
    } catch (e) {
      setError(e.message || 'Génération IA impossible');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!hasReplyId) {
      setError('Générez d\'abord une réponse IA pour pouvoir la publier.');
      return;
    }
    setError(null);
    setPublishing(true);
    try {
      await onPublish(review.reply_id, draft);
      onClose && onClose();
    } catch (e) {
      setError(e.message || 'Publication impossible');
    } finally {
      setPublishing(false);
    }
  };

  const handleIgnore = async () => {
    if (!hasReplyId) { onClose && onClose(); return; }
    setError(null);
    setIgnoring(true);
    try {
      await onIgnore(review.reply_id);
      onClose && onClose();
    } catch (e) {
      setError(e.message || 'Action impossible');
    } finally {
      setIgnoring(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Détail de l'avis" height="full">
      <div style={styles.head}>
        <Avatar name={review.author_name} photoUrl={review.profile_photo_url} size={44} />
        <div style={styles.headBody}>
          <div style={styles.name}>{review.author_name || 'Anonyme'}</div>
          <div style={styles.metaRow}>
            <StarRating rating={review.rating} size={14} />
            <span style={styles.date}>{formatAbsoluteDate(review.review_time)}</span>
          </div>
        </div>
      </div>

      {review.text && <p style={styles.verbatim}>{review.text}</p>}

      {fallbackMode && (
        <div style={styles.fallbackNotice}>
          ⚠️ Statut indisponible — la suggestion IA et la publication seront possibles dès rétablissement du backend.
        </div>
      )}

      {isPublished ? (
        <div style={styles.publishedBlock}>
          <div style={styles.publishedHeader}>
            <span style={styles.publishedTitle}>Votre réponse</span>
            <PillBadge label="Publié" variant="success" />
          </div>
          <p style={styles.publishedText}>{review.reply_text || ''}</p>
        </div>
      ) : (
        !fallbackMode && (
          <div style={styles.suggestionBlock}>
            <div style={styles.suggestionHeader}>
              <span aria-hidden>✨</span>
              <span style={styles.suggestionTitle}>Suggestion IA</span>
            </div>

            {!draft && !generating && peutGenererIa && (
              <button
                type="button"
                onClick={handleGenerate}
                style={styles.generateBtn}
              >
                Générer une réponse
              </button>
            )}

            {!draft && !generating && !peutGenererIa && (
              <p style={styles.lockedHint}>
                {/* Sur iOS, aucune formule n'est nommée (App Store 3.1.3(f)). */}
                {canShowPurchaseCta()
                  ? '🔒 Les réponses générées par IA sont incluses à partir de la formule Pro. Vous pouvez répondre manuellement ci-dessous.'
                  : '🔒 Les réponses générées par IA ne sont pas incluses dans votre formule actuelle. Vous pouvez répondre manuellement ci-dessous.'}
              </p>
            )}

            {generating && (
              <div style={styles.skeleton}>
                <div style={styles.skeletonLine} />
                <div style={{ ...styles.skeletonLine, width: '85%' }} />
                <div style={{ ...styles.skeletonLine, width: '60%' }} />
              </div>
            )}

            {!generating && draft && (
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={5}
                style={styles.textarea}
                placeholder="Votre réponse…"
              />
            )}

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.actions}>
              <button
                type="button"
                onClick={handlePublish}
                disabled={!gmbConnected || !draft || publishing || !hasReplyId}
                style={{
                  ...styles.publishBtn,
                  opacity: (!gmbConnected || !draft || publishing || !hasReplyId) ? 0.5 : 1,
                  cursor: (!gmbConnected || !draft || !hasReplyId) ? 'not-allowed' : 'pointer',
                }}
                title={!gmbConnected ? 'Connectez Google Business pour publier' : ''}
              >
                {publishing ? 'Publication…' : 'Publier la réponse'}
              </button>
              {!gmbConnected && (
                <div style={styles.gmbHint}>
                  🔒 Connectez Google Business pour publier
                </div>
              )}
              <button
                type="button"
                onClick={handleIgnore}
                disabled={ignoring}
                style={styles.ignoreBtn}
              >
                {ignoring ? 'En cours…' : 'Ignorer cet avis'}
              </button>
            </div>
          </div>
        )
      )}
    </BottomSheet>
  );
}

const styles = {
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
  },
  headBody: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
  },
  date: {
    fontSize: 12,
    color: '#6B6B6B',
  },
  verbatim: {
    margin: '0 0 16px',
    padding: 14,
    background: '#FFF5F0',
    borderRadius: 12,
    fontSize: 14,
    color: '#1A1A1A',
    lineHeight: 1.5,
  },
  fallbackNotice: {
    background: '#FFF4D6',
    color: '#1A1A1A',
    padding: 12,
    borderRadius: 12,
    fontSize: 13,
    lineHeight: 1.4,
  },
  publishedBlock: {
    background: '#E8F5EE',
    borderRadius: 12,
    padding: 14,
  },
  publishedHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  publishedTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#2D7A4A',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  publishedText: {
    margin: 0,
    fontSize: 14,
    color: '#1A1A1A',
    lineHeight: 1.5,
  },
  suggestionBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  suggestionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 14,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  suggestionTitle: {
    color: '#1A1A1A',
  },
  lockedHint: {
    margin: '4px 0 10px',
    fontSize: 13,
    lineHeight: 1.5,
    color: '#6B6B6B',
  },
  generateBtn: {
    background: '#1A1A1A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 12,
    padding: '14px 16px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    border: '1px solid #E5E5E0',
    borderRadius: 12,
    padding: 12,
    fontSize: 14,
    fontFamily: 'inherit',
    color: '#1A1A1A',
    background: '#FFFFFF',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
    lineHeight: 1.5,
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: 12,
    background: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #EDEAE3',
  },
  skeletonLine: {
    height: 10,
    background: '#EDEAE3',
    borderRadius: 6,
    width: '100%',
    animation: 'revyoPulse 1.4s ease-in-out infinite',
  },
  error: {
    color: '#C8442A',
    fontSize: 13,
    background: '#FDEAE5',
    borderRadius: 10,
    padding: 10,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 4,
  },
  publishBtn: {
    background: '#E8502A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 12,
    padding: '14px 16px',
    fontSize: 15,
    fontWeight: 700,
    fontFamily: 'inherit',
  },
  gmbHint: {
    textAlign: 'center',
    fontSize: 12,
    color: '#6B6B6B',
  },
  ignoreBtn: {
    background: 'transparent',
    color: '#6B6B6B',
    border: 'none',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: 8,
  },
};
